import React, { useState, useEffect } from "react";
import {
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  useTheme,
  Pagination,
  Skeleton,
  Card,
  CardContent,
  Typography,
  Grid,
  TableSortLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
} from "@mui/material";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowUpward from "@mui/icons-material/ArrowUpward";

interface IDataTableColumn {
  header: string;
  key: string;
  render?: (value: any) => React.ReactNode;
  /** When true, this column is excluded from sort UI (e.g. actions). */
  disableSort?: boolean;
}

export interface IDataTableProps {
  columns: IDataTableColumn[];
  data: any[];
  page: number;
  isLoading: boolean;
  onChange: (page: number) => void;
  total: number;
  rowsPerPage?: number;
  showPagination?: boolean;
  /** When true, enables horizontal scrolling for wide tables (e.g. many columns). */
  horizontalScroll?: boolean;
  /** When true, shows sort controls on headers (desktop) and a compact bar (mobile). Requires onSortChange. */
  sortable?: boolean;
  sortColumn?: string | null;
  sortDirection?: "asc" | "desc";
  /** Toggle asc/desc on same column; set asc when switching column. Use key "" from mobile to clear sort (if supported by parent). */
  onSortChange?: (columnKey: string) => void;
}

const DataTable = ({
  columns,
  data,
  total,
  page,
  isLoading,
  onChange,
  rowsPerPage = 10,
  showPagination = true,
  horizontalScroll = false,
  sortable = false,
  sortColumn = null,
  sortDirection = "asc",
  onSortChange,
}: IDataTableProps) => {
  const theme = useTheme();
  const [isMobile, setIsMobile] = useState(false);
  const count = Math.ceil(total / rowsPerPage);
  const showSortUi = Boolean(sortable && onSortChange);
  const isSortableColumn = (column: IDataTableColumn) =>
    !column.disableSort && column.key !== "actions";

  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      const isMobileWidth = width < 800;
      setIsMobile(isMobileWidth);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  if (isLoading) {
    if (isMobile) {
      return (
        <Box>
          {Array.from({ length: 5 }).map((_, index) => (
            <Card key={index} sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent>
                {columns.map((column) => (
                  <Box key={column.key} sx={{ mb: 1 }}>
                    <Skeleton variant="text" width="30%" height={16} />
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={20}
                      sx={{ mt: 0.5, borderRadius: 1 }}
                    />
                  </Box>
                ))}
              </CardContent>
            </Card>
          ))}
          <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
            <Skeleton variant="rounded" width={300} height={32} />
          </Box>
        </Box>
      );
    }

    return (
      <TableContainer
        component={Paper}
        sx={{
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 119, 194, 0.1)",
          border: `1px solid ${theme.palette.primary.light}20`,
          overflow: "auto",
          maxWidth: "100%",
          width: "100%",
        }}
      >
        <Table
          sx={{
            minWidth: horizontalScroll ? 650 : undefined,
            width: horizontalScroll ? "max-content" : "100%",
            tableLayout: horizontalScroll ? "auto" : "fixed",
          }}
          aria-label="data table loading"
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: theme.palette.primary.main,
                height: 40,
              }}
            >
              {columns.map((column) => (
                <TableCell key={column.key}>
                  <Skeleton
                    variant="text"
                    sx={{
                      bgcolor: "rgba(255, 255, 255, 0.2)",
                      height: 20,
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.from({ length: 5 }).map((_, index) => (
              <TableRow key={index} sx={{ height: 40 }}>
                {columns.map((column) => (
                  <TableCell key={column.key}>
                    <Skeleton
                      variant="rectangular"
                      sx={{
                        width: "100%",
                        height: 20,
                        borderRadius: 1,
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <Skeleton variant="rounded" width={300} height={32} />
        </Box>
      </TableContainer>
    );
  }

  if (isMobile) {
    const sortableColumns = columns.filter(isSortableColumn);
    return (
      <Box>
        {showSortUi && sortableColumns.length > 0 && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              alignItems: "center",
            }}
          >
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="datatable-sort-column-label">
                Ordenar por
              </InputLabel>
              <Select
                labelId="datatable-sort-column-label"
                label="Ordenar por"
                value={sortColumn ?? ""}
                onChange={(e) => {
                  onSortChange?.(e.target.value as string);
                }}
              >
                <MenuItem value="">
                  <em>Sin ordenar</em>
                </MenuItem>
                {sortableColumns.map((c) => (
                  <MenuItem key={c.key} value={c.key}>
                    {c.header}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {sortColumn ? (
              <IconButton
                size="small"
                aria-label="Invertir orden"
                onClick={() => onSortChange?.(sortColumn)}
              >
                {sortDirection === "asc" ? (
                  <ArrowUpward fontSize="small" />
                ) : (
                  <ArrowDownward fontSize="small" />
                )}
              </IconButton>
            ) : null}
          </Box>
        )}
        {data.map((row, index) => (
          <Card
            // key={row.userId || row.id || index}
            key={row.userId || row.name || row.id || index}
            sx={{
              mb: 2,
              borderRadius: 2,
              boxShadow: "0 2px 8px rgba(0, 119, 194, 0.1)",
              border: `1px solid ${theme.palette.primary.light}20`,
              "&:hover": {
                transform: "translateY(-2px)",
                boxShadow: "0 4px 16px rgba(0, 119, 194, 0.15)",
                transition: "all 0.2s ease-in-out",
              },
              transition: "all 0.2s ease-in-out",
            }}
          >
            <CardContent>
              <Grid container spacing={2}>
                {columns.map((column) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={column.key}>
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.primary.main,
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          fontSize: "0.7rem",
                        }}
                      >
                        {column.header}
                      </Typography>
                      <Box sx={{ mt: 0.5 }}>
                        {column.key === "actions" ? (
                          <Box sx={{ display: "flex", gap: 1 }}>
                            {column?.render ? column.render(row) : null}
                          </Box>
                        ) : column.render ? (
                          column.render(row)
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.text.primary }}
                          >
                            {row[column.key]}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        ))}

        <Pagination
          count={count}
          page={page}
          onChange={(event, value) => {
            onChange(value);
          }}
          color="primary"
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            textAlign: "center",
            mt: 2,
            mb: 2,
          }}
        />
      </Box>
    );
  }

  return (
    <TableContainer
      component={Paper}
      sx={{
        borderRadius: 3,
        boxShadow: "0 4px 20px rgba(0, 119, 194, 0.1)",
        border: `1px solid ${theme.palette.primary.light}20`,
        overflow: "auto",
        maxWidth: "100%",
        width: "100%",
      }}
    >
      <Table
        sx={{
          minWidth: horizontalScroll ? 650 : undefined,
          width: horizontalScroll ? "max-content" : "100%",
          tableLayout: horizontalScroll ? "auto" : "fixed",
        }}
        aria-label="data table"
      >
        <TableHead>
          <TableRow
            sx={{
              backgroundColor: theme.palette.primary.main,
              "& .MuiTableCell-root": {
                borderBottom: `2px solid ${theme.palette.primary.dark}`,
                color: theme.palette.background.paper,
                fontWeight: "bold",
                fontSize: "0.95rem",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              },
            }}
          >
            {columns.map((column) => (
              <TableCell
                key={column.key}
                sortDirection={
                  showSortUi &&
                  isSortableColumn(column) &&
                  sortColumn === column.key
                    ? sortDirection
                    : false
                }
              >
                {showSortUi && isSortableColumn(column) ? (
                  <TableSortLabel
                    active={sortColumn === column.key}
                    direction={
                      sortColumn === column.key ? sortDirection : "asc"
                    }
                    onClick={() => onSortChange?.(column.key)}
                  >
                    {column.header}
                  </TableSortLabel>
                ) : (
                  column.header
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((row, index) => (
            <TableRow
              // key={row.userId || row.id || index}
              key={row.userId || row.name || row.id || index}
              sx={{
                "&:last-child td, &:last-child th": { border: 0 },
                "&:nth-of-type(even)": {
                  backgroundColor: theme.palette.primary.light + "05",
                },
                "&:hover": {
                  backgroundColor: theme.palette.primary.light + "10",
                  transform: "translateY(-1px)",
                  boxShadow: `0 2px 8px ${theme.palette.primary.light}30`,
                  transition: "all 0.2s ease-in-out",
                },
                transition: "all 0.2s ease-in-out",
                "& .MuiTableCell-root": {
                  borderBottom: `1px solid ${theme.palette.primary.light}20`,
                  color: theme.palette.text.primary,
                  fontSize: "0.9rem",
                },
              }}
            >
              {columns.map((column) => (
                <TableCell key={column.key}>
                  {column.key === "actions" ? (
                    <Box sx={{ display: "flex", gap: 1 }}>
                      {column?.render ? column.render(row) : null}
                    </Box>
                  ) : column.render ? (
                    column.render(row)
                  ) : (
                    row[column.key]
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {showPagination && (
        <Pagination
          count={count}
          page={page}
          onChange={(event, value) => {
            onChange(value);
          }}
          color="primary"
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            textAlign: "center",
            mt: 2,
            mb: 2,
          }}
        />
      )}
    </TableContainer>
  );
};

export default DataTable;
